import { getBackendUrl } from "@Utils";
import { t } from "@i18n";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosWrapper from "@AxiosWrapper";
import { showToastNotification } from "../reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "../services/constants";
import type {
  GetFolderRolesListResponse,
  GetFolderRolesResponse,
  SetFolderRoleResponse,
  IsFile403Response,
  SpaceToggleResponse,
} from "@ServerTypes/folders/folders.schema";

export const getSpaceUserListForFolder = createAsyncThunk(
  "accessPermission/getSpaceUserListForFolder",
  async (_, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.get<GetFolderRolesListResponse>(
        `${getBackendUrl()}/folders/getFolderRolesList`,
      );
      if (responseData.data.status) {
        return (responseData.data as any).data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

export const getFolderRoles = createAsyncThunk("accessPermission/getFolderRoles", async (data, { rejectWithValue }) => {
  try {
    const responseData = await axiosWrapper.post<GetFolderRolesResponse>(
      `${getBackendUrl()}/folders/getFolderRoles`,
      data,
    );
    if (responseData.data.status) {
      return (responseData.data as any).data;
    }
    return rejectWithValue(responseData.data);
  } catch (error: unknown) {
    return rejectWithValue(
      (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
    );
  }
});

export const setDataForFolder = createAsyncThunk(
  "accessPermission/setDataForFolder",
  async (data: any, { dispatch, rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<SetFolderRoleResponse>(
        `${getBackendUrl()}/folders/setFolderRole`,
        data,
      );
      if (responseData.data.status) {
        dispatch(showToastNotification({ content: t("Permission changed successfully!") }));
        return responseData.data;
      }
      dispatch(
        showToastNotification({
          content: t("Insufficient permission access."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      dispatch(
        showToastNotification({
          content: t("Insufficient permission access."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

export const setAccessPermission = createAsyncThunk(
  "accessPermission/setAccessPermission",
  async (data, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<IsFile403Response>(`${getBackendUrl()}/folders/isFile403`, data);
      if (responseData.data.status) {
        return responseData.data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

export const setSpaceToggle = createAsyncThunk(
  "accessPermission/setSpaceToggle",
  async (data: any, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<SpaceToggleResponse>(`${getBackendUrl()}/folders/spaceToggle`, data);
      if (responseData.data.status) {
        return responseData.data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

const spaceUserDataForFolderSlice = createSlice({
  name: "spaceUserDataForFolder",
  initialState: [],
  reducers: {
    setSpaceUserListForFolder: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(getSpaceUserListForFolder.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const folderRolesSlice = createSlice({
  name: "folderRoles",
  initialState: [],
  reducers: {
    setFolderRoles: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(getFolderRoles.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const folderDataSlice = createSlice({
  name: "folderData",
  initialState: [],
  reducers: {
    setFolderData: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(setDataForFolder.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const accessPermissionSlice = createSlice({
  name: "accessPermission",
  initialState: {},
  reducers: {
    setAccessPermission: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(setAccessPermission.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const spaceToggleSlice = createSlice({
  name: "spaceToggle",
  initialState: {} as any,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setSpaceToggle.fulfilled, (_state, action) => action.payload);
  },
});

export const spaceUserDataForFolder = spaceUserDataForFolderSlice.reducer;
export const setFolderRoles = folderRolesSlice.reducer;
export const setFolderData = folderDataSlice.reducer;
export const getAccessPermission = accessPermissionSlice.reducer;
export const getSpaceToggle = spaceToggleSlice.reducer;
